<div class="relative w-full" data-testid="advanced-datepicker-container">
  <!-- Input Field -->
  <div class="relative">
    <input
      #inputRef
      data-testid="advanced-datepicker-input"
      type="text"
      [value]="displayValue()"
      [placeholder]="placeholder || selectDatePlaceholder()"
      [disabled]="disabled"
      (focus)="openPopup()"
      (blur)="onInputBlur($event)"
      class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:border-sky-500 focus:border disabled:bg-gray-100 disabled:text-[#6b7280] disabled:cursor-not-allowed text-sm transition-colors placeholder:text-gray-500"
    />
    <button
      #iconButtonRef
      data-testid="advanced-datepicker-toggle-btn"
      type="button"
      (click)="onIconClick($event)"
      [disabled]="disabled"
      class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
    >
      <span class="material-symbols-outlined text-xl">{{
        timeOnly ? "schedule" : "calendar_today"
      }}</span>
    </button>
  </div>

  <!-- Popup -->
  @if (isOpen()) {
    <div
      #popupRef
      data-testid="advanced-datepicker-popup"
      class="z-50 bg-white rounded-xl shadow-xl border border-gray-200 p-4 min-w-[320px] datepicker-popup"
      [class.absolute]="!appendToBody"
      [class.mt-1]="!appendToBody"
      [class.w-full]="!appendToBody"
      [ngStyle]="appendToBody ? bodyPopupStyle() : null"
    >
      <!-- Calendar Header -->
      @if (!timeOnly && currentView() === "calendar") {
        <div class="flex items-center justify-between mb-4">
          <button
            data-testid="advanced-datepicker-previous-month-btn"
            type="button"
            (click)="previousMonth()"
            class="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <span class="material-symbols-outlined text-gray-600">
              chevron_left
            </span>
          </button>
          <button
            data-testid="advanced-datepicker-month-year-toggle-btn"
            type="button"
            (click)="toggleMonthYearView()"
            class="px-3 py-1 text-sm font-semibold text-gray-800 hover:bg-gray-100 rounded transition-colors"
          >
            {{ monthYearLabel() }}
          </button>
          <button
            data-testid="advanced-datepicker-next-month-btn"
            type="button"
            (click)="nextMonth()"
            class="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <span class="material-symbols-outlined text-gray-600">
              chevron_right
            </span>
          </button>
        </div>

        <!-- Day of Week Header -->
        <div class="grid grid-cols-7 gap-1 mb-2">
          @for (day of dayNames(); track day) {
            <div
              class="text-xs uppercase font-medium text-gray-400 text-center py-1"
            >
              {{ day }}
            </div>
          }
        </div>

        <!-- Calendar Grid -->
        <div class="grid grid-cols-7 gap-1">
          @for (day of calendarDays(); track day.date.getTime()) {
            <button
              [attr.data-testid]="
                'advanced-datepicker-day-btn-' + day.date.getTime()
              "
              type="button"
              (mousedown)="selectDate(day); $event.preventDefault()"
              (mouseenter)="hoveredDate.set(day.date)"
              (mouseleave)="hoveredDate.set(null)"
              [disabled]="isDateDisabled(day.date)"
              [ngClass]="getDayClasses(day)"
            >
              {{ day.date.getDate() }}
            </button>
          }
        </div>
      }

      <!-- Month-Year Picker View -->
      @if (!timeOnly && currentView() === "month-year") {
        <div class="mb-4">
          <div class="flex items-center justify-between mb-4">
            <button
              data-testid="advanced-datepicker-previous-year-btn"
              type="button"
              (click)="previousMonth()"
              class="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <span class="material-symbols-outlined text-gray-600">
                chevron_left
              </span>
            </button>
            <div class="text-lg font-semibold text-gray-800">
              {{ currentYear() }}
            </div>
            <button
              data-testid="advanced-datepicker-next-year-btn"
              type="button"
              (click)="nextMonth()"
              class="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <span class="material-symbols-outlined text-gray-600">
                chevron_right
              </span>
            </button>
          </div>
          <div class="grid grid-cols-3 gap-2">
            @for (month of monthNames(); track month; let i = $index) {
              <button
                [attr.data-testid]="'advanced-datepicker-month-btn-' + i"
                type="button"
                (click)="selectMonth(i)"
                [class.bg-sky-500]="i === currentMonth()"
                [class.text-white]="i === currentMonth()"
                [class.text-gray-800]="i !== currentMonth()"
                class="px-4 py-2 text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors"
              >
                {{ month }}
              </button>
            }
          </div>
        </div>
      }

      <!-- Time Picker -->
      @if (
        (showTime || timeOnly) && (timeOnly || currentView() === "calendar")
      ) {
        <div
          [class]="
            timeOnly ? '' : 'mt-4 pt-4 border-t border-gray-200'
          "
        >
          @if (mode === "single") {
            <div class="flex items-center justify-center gap-2">
              <div class="flex items-center gap-1">
                <!-- Hour -->
                <div class="flex flex-col items-center">
                  <button
                    data-testid="advanced-datepicker-start-hour-increment-btn"
                    type="button"
                    (click)="incrementHour('start')"
                    class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                  >
                    <span
                      class="material-symbols-outlined text-gray-600 text-sm"
                    >
                      keyboard_arrow_up
                    </span>
                  </button>
                  <input
                    data-testid="advanced-datepicker-start-hour-input"
                    type="number"
                    [value]="startHour().toString().padStart(2, '0')"
                    (input)="onHourInput('start', $event)"
                    (blur)="onHourBlur('start', $event)"
                    [min]="timeFormat === '24h' ? 0 : 1"
                    [max]="timeFormat === '24h' ? 23 : 12"
                    class="w-12 px-2 py-1 text-center text-sm font-medium text-gray-800 border border-gray-300 rounded-md bg-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                  />
                  <button
                    data-testid="advanced-datepicker-start-hour-decrement-btn"
                    type="button"
                    (click)="decrementHour('start')"
                    class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                  >
                    <span
                      class="material-symbols-outlined text-gray-600 text-sm"
                    >
                      keyboard_arrow_down
                    </span>
                  </button>
                </div>
                <span class="text-gray-500 font-medium mx-0.5">:</span>
                <!-- Minute -->
                <div class="flex flex-col items-center">
                  <button
                    data-testid="advanced-datepicker-start-minute-increment-btn"
                    type="button"
                    (click)="incrementMinute('start')"
                    class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                  >
                    <span
                      class="material-symbols-outlined text-gray-600 text-sm"
                    >
                      keyboard_arrow_up
                    </span>
                  </button>
                  <input
                    data-testid="advanced-datepicker-start-minute-input"
                    type="number"
                    [value]="startMinute().toString().padStart(2, '0')"
                    (input)="onMinuteInput('start', $event)"
                    (blur)="onMinuteBlur('start', $event)"
                    min="0"
                    max="59"
                    class="w-12 px-2 py-1 text-center text-sm font-medium text-gray-800 border border-gray-300 rounded-md bg-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                  />
                  <button
                    data-testid="advanced-datepicker-start-minute-decrement-btn"
                    type="button"
                    (click)="decrementMinute('start')"
                    class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                  >
                    <span
                      class="material-symbols-outlined text-gray-600 text-sm"
                    >
                      keyboard_arrow_down
                    </span>
                  </button>
                </div>
                @if (showSeconds) {
                  <span class="text-gray-500 font-medium mx-0.5">:</span>
                  <div class="flex flex-col items-center">
                    <button
                      data-testid="advanced-datepicker-start-second-increment-btn"
                      type="button"
                      (click)="incrementSecond('start')"
                      class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                    >
                      <span
                        class="material-symbols-outlined text-gray-600 text-sm"
                      >
                        keyboard_arrow_up
                      </span>
                    </button>
                    <input
                      data-testid="advanced-datepicker-start-second-input"
                      type="number"
                      [value]="startSecond().toString().padStart(2, '0')"
                      (input)="onSecondInput('start', $event)"
                      (blur)="onSecondBlur('start', $event)"
                      min="0"
                      max="59"
                      class="w-12 px-2 py-1 text-center text-sm font-medium text-gray-800 border border-gray-300 rounded-md bg-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                    />
                    <button
                      data-testid="advanced-datepicker-start-second-decrement-btn"
                      type="button"
                      (click)="decrementSecond('start')"
                      class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                    >
                      <span
                        class="material-symbols-outlined text-gray-600 text-sm"
                      >
                        keyboard_arrow_down
                      </span>
                    </button>
                  </div>
                }
                @if (timeFormat === "12h") {
                  <span class="text-gray-500 font-medium mx-0.5">:</span>
                  <!-- AM/PM -->
                  <div class="flex flex-col items-center">
                    <button
                      data-testid="advanced-datepicker-start-ampm-increment-btn"
                      type="button"
                      (click)="toggleAmPm('start')"
                      class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                    >
                      <span
                        class="material-symbols-outlined text-gray-600 text-sm"
                      >
                        keyboard_arrow_up
                      </span>
                    </button>
                    <div
                      class="w-12 px-2 py-1 text-center text-sm font-medium text-gray-800 border border-gray-300 rounded-md bg-white"
                    >
                      {{ startAmPm() }}
                    </div>
                    <button
                      data-testid="advanced-datepicker-start-ampm-decrement-btn"
                      type="button"
                      (click)="toggleAmPm('start')"
                      class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                    >
                      <span
                        class="material-symbols-outlined text-gray-600 text-sm"
                      >
                        keyboard_arrow_down
                      </span>
                    </button>
                  </div>
                }
              </div>
            </div>
          } @else {
            <div class="space-y-3">
              <div class="flex items-center justify-center gap-3">
                <span
                  class="text-sm font-medium text-gray-700 w-12 whitespace-nowrap"
                  >{{ fromLabel() }}:</span
                >
                <div class="flex items-center gap-1">
                  <!-- Hour -->
                  <div class="flex flex-col items-center">
                    <button
                      data-testid="advanced-datepicker-range-start-hour-increment-btn"
                      type="button"
                      (click)="incrementHour('start')"
                      class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                    >
                      <span
                        class="material-symbols-outlined text-gray-600 text-sm"
                      >
                        keyboard_arrow_up
                      </span>
                    </button>
                    <input
                      data-testid="advanced-datepicker-range-start-hour-input"
                      type="number"
                      [value]="startHour().toString().padStart(2, '0')"
                      (input)="onHourInput('start', $event)"
                      (blur)="onHourBlur('start', $event)"
                      [min]="timeFormat === '24h' ? 0 : 1"
                      [max]="timeFormat === '24h' ? 23 : 12"
                      class="w-12 px-2 py-1 text-center text-sm font-medium text-gray-800 border border-gray-300 rounded-md bg-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                    />
                    <button
                      data-testid="advanced-datepicker-range-start-hour-decrement-btn"
                      type="button"
                      (click)="decrementHour('start')"
                      class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                    >
                      <span
                        class="material-symbols-outlined text-gray-600 text-sm"
                      >
                        keyboard_arrow_down
                      </span>
                    </button>
                  </div>
                  <span class="text-gray-500 font-medium mx-0.5">:</span>
                  <!-- Minute -->
                  <div class="flex flex-col items-center">
                    <button
                      data-testid="advanced-datepicker-range-start-minute-increment-btn"
                      type="button"
                      (click)="incrementMinute('start')"
                      class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                    >
                      <span
                        class="material-symbols-outlined text-gray-600 text-sm"
                      >
                        keyboard_arrow_up
                      </span>
                    </button>
                    <input
                      data-testid="advanced-datepicker-range-start-minute-input"
                      type="number"
                      [value]="startMinute().toString().padStart(2, '0')"
                      (input)="onMinuteInput('start', $event)"
                      (blur)="onMinuteBlur('start', $event)"
                      min="0"
                      max="59"
                      class="w-12 px-2 py-1 text-center text-sm font-medium text-gray-800 border border-gray-300 rounded-md bg-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                    />
                    <button
                      data-testid="advanced-datepicker-range-start-minute-decrement-btn"
                      type="button"
                      (click)="decrementMinute('start')"
                      class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                    >
                      <span
                        class="material-symbols-outlined text-gray-600 text-sm"
                      >
                        keyboard_arrow_down
                      </span>
                    </button>
                  </div>
                  @if (showSeconds) {
                    <span class="text-gray-500 font-medium mx-0.5">:</span>
                    <div class="flex flex-col items-center">
                      <button
                        data-testid="advanced-datepicker-range-start-second-increment-btn"
                        type="button"
                        (click)="incrementSecond('start')"
                        class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                      >
                        <span
                          class="material-symbols-outlined text-gray-600 text-sm"
                        >
                          keyboard_arrow_up
                        </span>
                      </button>
                      <input
                        data-testid="advanced-datepicker-range-start-second-input"
                        type="number"
                        [value]="startSecond().toString().padStart(2, '0')"
                        (input)="onSecondInput('start', $event)"
                        (blur)="onSecondBlur('start', $event)"
                        min="0"
                        max="59"
                        class="w-12 px-2 py-1 text-center text-sm font-medium text-gray-800 border border-gray-300 rounded-md bg-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                      />
                      <button
                        data-testid="advanced-datepicker-range-start-second-decrement-btn"
                        type="button"
                        (click)="decrementSecond('start')"
                        class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                      >
                        <span
                          class="material-symbols-outlined text-gray-600 text-sm"
                        >
                          keyboard_arrow_down
                        </span>
                      </button>
                    </div>
                  }
                  @if (timeFormat === "12h") {
                    <span class="text-gray-500 font-medium mx-0.5">:</span>
                    <!-- AM/PM -->
                    <div class="flex flex-col items-center">
                      <button
                        data-testid="advanced-datepicker-range-start-ampm-increment-btn"
                        type="button"
                        (click)="toggleAmPm('start')"
                        class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                      >
                        <span
                          class="material-symbols-outlined text-gray-600 text-sm"
                        >
                          keyboard_arrow_up
                        </span>
                      </button>
                      <div
                        class="w-12 px-2 py-1 text-center text-sm font-medium text-gray-800 border border-gray-300 rounded-md bg-white"
                      >
                        {{ startAmPm() }}
                      </div>
                      <button
                        data-testid="advanced-datepicker-range-start-ampm-decrement-btn"
                        type="button"
                        (click)="toggleAmPm('start')"
                        class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                      >
                        <span
                          class="material-symbols-outlined text-gray-600 text-sm"
                        >
                          keyboard_arrow_down
                        </span>
                      </button>
                    </div>
                  }
                </div>
              </div>
              <div class="flex items-center justify-center gap-3">
                <span
                  class="text-sm font-medium text-gray-700 w-12 whitespace-nowrap"
                  >{{ toLabel() }}:</span
                >
                <div class="flex items-center gap-1">
                  <!-- Hour -->
                  <div class="flex flex-col items-center">
                    <button
                      data-testid="advanced-datepicker-range-end-hour-increment-btn"
                      type="button"
                      (click)="incrementHour('end')"
                      class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                    >
                      <span
                        class="material-symbols-outlined text-gray-600 text-sm"
                      >
                        keyboard_arrow_up
                      </span>
                    </button>
                    <input
                      data-testid="advanced-datepicker-range-end-hour-input"
                      type="number"
                      [value]="endHour().toString().padStart(2, '0')"
                      (input)="onHourInput('end', $event)"
                      (blur)="onHourBlur('end', $event)"
                      [min]="timeFormat === '24h' ? 0 : 1"
                      [max]="timeFormat === '24h' ? 23 : 12"
                      class="w-12 px-2 py-1 text-center text-sm font-medium text-gray-800 border border-gray-300 rounded-md bg-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                    />
                    <button
                      data-testid="advanced-datepicker-range-end-hour-decrement-btn"
                      type="button"
                      (click)="decrementHour('end')"
                      class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                    >
                      <span
                        class="material-symbols-outlined text-gray-600 text-sm"
                      >
                        keyboard_arrow_down
                      </span>
                    </button>
                  </div>
                  <span class="text-gray-500 font-medium mx-0.5">:</span>
                  <!-- Minute -->
                  <div class="flex flex-col items-center">
                    <button
                      data-testid="advanced-datepicker-range-end-minute-increment-btn"
                      type="button"
                      (click)="incrementMinute('end')"
                      class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                    >
                      <span
                        class="material-symbols-outlined text-gray-600 text-sm"
                      >
                        keyboard_arrow_up
                      </span>
                    </button>
                    <input
                      data-testid="advanced-datepicker-range-end-minute-input"
                      type="number"
                      [value]="endMinute().toString().padStart(2, '0')"
                      (input)="onMinuteInput('end', $event)"
                      (blur)="onMinuteBlur('end', $event)"
                      min="0"
                      max="59"
                      class="w-12 px-2 py-1 text-center text-sm font-medium text-gray-800 border border-gray-300 rounded-md bg-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                    />
                    <button
                      data-testid="advanced-datepicker-range-end-minute-decrement-btn"
                      type="button"
                      (click)="decrementMinute('end')"
                      class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                    >
                      <span
                        class="material-symbols-outlined text-gray-600 text-sm"
                      >
                        keyboard_arrow_down
                      </span>
                    </button>
                  </div>
                  @if (showSeconds) {
                    <span class="text-gray-500 font-medium mx-0.5">:</span>
                    <div class="flex flex-col items-center">
                      <button
                        data-testid="advanced-datepicker-range-end-second-increment-btn"
                        type="button"
                        (click)="incrementSecond('end')"
                        class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                      >
                        <span
                          class="material-symbols-outlined text-gray-600 text-sm"
                        >
                          keyboard_arrow_up
                        </span>
                      </button>
                      <input
                        data-testid="advanced-datepicker-range-end-second-input"
                        type="number"
                        [value]="endSecond().toString().padStart(2, '0')"
                        (input)="onSecondInput('end', $event)"
                        (blur)="onSecondBlur('end', $event)"
                        min="0"
                        max="59"
                        class="w-12 px-2 py-1 text-center text-sm font-medium text-gray-800 border border-gray-300 rounded-md bg-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                      />
                      <button
                        data-testid="advanced-datepicker-range-end-second-decrement-btn"
                        type="button"
                        (click)="decrementSecond('end')"
                        class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                      >
                        <span
                          class="material-symbols-outlined text-gray-600 text-sm"
                        >
                          keyboard_arrow_down
                        </span>
                      </button>
                    </div>
                  }
                  @if (timeFormat === "12h") {
                    <span class="text-gray-500 font-medium mx-0.5">:</span>
                    <!-- AM/PM -->
                    <div class="flex flex-col items-center">
                      <button
                        data-testid="advanced-datepicker-range-end-ampm-increment-btn"
                        type="button"
                        (click)="toggleAmPm('end')"
                        class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                      >
                        <span
                          class="material-symbols-outlined text-gray-600 text-sm"
                        >
                          keyboard_arrow_up
                        </span>
                      </button>
                      <div
                        class="w-12 px-2 py-1 text-center text-sm font-medium text-gray-800 border border-gray-300 rounded-md bg-white"
                      >
                        {{ endAmPm() }}
                      </div>
                      <button
                        data-testid="advanced-datepicker-range-end-ampm-decrement-btn"
                        type="button"
                        (click)="toggleAmPm('end')"
                        class="p-0.5 hover:bg-gray-100 rounded transition-colors"
                      >
                        <span
                          class="material-symbols-outlined text-gray-600 text-sm"
                        >
                          keyboard_arrow_down
                        </span>
                      </button>
                    </div>
                  }
                </div>
              </div>
            </div>
          }
        </div>
      }

      <!-- Today and Reset Buttons -->
      @if (currentView() === "calendar") {
        <div
          class="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between"
        >
          <button
            data-testid="advanced-datepicker-today-btn"
            type="button"
            (click)="selectToday()"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 transition-colors"
          >
            {{ todayLabel() }}
          </button>
          <button
            data-testid="advanced-datepicker-reset-btn"
            type="button"
            (click)="resetDate()"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 transition-colors"
          >
            {{ resetLabel() }}
          </button>
        </div>
      }
    </div>
  }
</div>
